"use client";

import Link from "next/link";
import { useApp } from "./AppProvider";
import { QumrahLogo } from "./Navbar";
import type { FooterData, SiteSettings, ServiceItem } from "@/lib/cms";

interface FooterProps {
  data: FooterData;
  site: SiteSettings;
  services: ServiceItem[];
}

export default function Footer({ data, site, services }: FooterProps) {
  const { isRTL } = useApp();
  const h = (en: string, ar: string) => isRTL ? (ar || en) : en;

  const serviceLinks = services.map((s) => ({
    label: h(s.title_en, s.title_ar),
    href: `/${s.slug}`,
  }));

  return (
    <footer id="contact" style={{ borderTop: "1px solid var(--border-subtle)", padding: "3rem 0 2rem", transition: "border-color 0.4s ease" }}>
      <div className="container">
        <div className="grid-4col" style={{ marginBottom: "3rem", direction: isRTL ? "rtl" : "ltr" }}>
          {/* Brand column */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", flexDirection: isRTL ? "row-reverse" : "row" }}>
              {site.logo?.url ? (
                <img src={site.logo.url} alt={site.siteName} style={{ height: 36, width: "auto" }} />
              ) : (
                <QumrahLogo size={36} />
              )}
              <div style={{ textAlign: isRTL ? "right" : "left" }}>
                <div style={{ fontWeight: 900, fontSize: "1rem", letterSpacing: "0.06em", color: "var(--text-primary)" }}>{site.siteName}</div>
                <div style={{ fontSize: "0.65rem", color: "var(--color-medium-gray)", letterSpacing: "0.15em" }}>{site.tagline.toUpperCase()}</div>
              </div>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", lineHeight: 1.75, maxWidth: 280, textAlign: isRTL ? "right" : "left" }}>
              {h(data.description_en, data.description_ar)}
            </p>
            {/* Contact info */}
            <div style={{ marginTop: "1.2rem", textAlign: isRTL ? "right" : "left" }}>
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.4rem" }}>{site.primaryEmail}</div>
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.4rem" }}>{site.primaryPhone}</div>
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem" }}>{h(site.address_en, site.address_ar)}</div>
            </div>
          </div>

          {/* Services column */}
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <h4 style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: "1.2rem", color: "var(--text-primary)" }}>
              {h(data.col_services_en, data.col_services_ar)}
            </h4>
            {serviceLinks.slice(0, 5).map((l) => (
              <Link key={l.label} href={l.href} style={{ display: "block", color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.7rem", transition: "color 0.3s ease", textDecoration: "none" }}
                onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--color-pink)")}
                onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--text-muted)")}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Company column */}
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <h4 style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: "1.2rem", color: "var(--text-primary)" }}>
              {h(data.col_company_en, data.col_company_ar)}
            </h4>
            {data.companyLinks.map((l) => (
              <a key={l.label_en} href={l.href} style={{ display: "block", color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.7rem", cursor: "pointer", transition: "color 0.3s ease", textDecoration: "none" }}
                onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--color-pink)")}
                onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--text-muted)")}>
                {h(l.label_en, l.label_ar)}
              </a>
            ))}
          </div>

          {/* Contact column */}
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <h4 style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: "1.2rem", color: "var(--text-primary)" }}>
              {h(data.col_contact_en, data.col_contact_ar)}
            </h4>
            <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.7rem" }}>{site.primaryEmail}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.7rem" }}>{site.primaryPhone}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "0.7rem" }}>{h(site.address_en, site.address_ar)}</div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", direction: isRTL ? "rtl" : "ltr" }}>
          <div style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
            {h(site.copyright_en, site.copyright_ar)}
          </div>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            {data.legalLinks.map((l) => (
              <a key={l.label_en} href={l.href} style={{ color: "var(--text-muted)", fontSize: "0.8rem", cursor: "pointer", textDecoration: "none" }}>
                {h(l.label_en, l.label_ar)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
